import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'

/**
 * Rutan "För läkare och sjuksköterskor" (IM-198). Text, länkar och utseende
 * är desamma som huvudsidans `app/components/only-for-medical.tsx` i
 * webbrepot; ändras texten där ska den ändras här också.
 *
 * Kakan delas med www: den sätts med Domain=.internetmedicin.se, så ett
 * godkännande här gäller också på huvudsidan (dess `cookieExists` läser
 * document.cookie, som innehåller domänkakor). Åt andra hållet når
 * huvudsidans kaka hit först när webben också sätter Domain (IM-199).
 * Bara startsidan monterar rutan; i /embed visar huvudsidan redan sin egen.
 *
 * Filen beror bara på React, heroicons och IM-paletten i Tailwind, så att
 * verktyg.internetmedicin.se kan ta in den oförändrad om den ska ha rutan
 * (separat beslut). Domänkakan gäller redan där.
 */
export const MEDICAL_AUDIENCE_COOKIE = 'medicalAudiencesV2'
export const MEDICAL_AUDIENCE_ACCEPTED_EVENT = 'im:medical-audience-accepted'
// Förlängs vid varje besök, som på huvudsidan.
const MAX_AGE_IN_SECONDS = 60 * 60 * 24 * 90 // tre månader
const SHARED_DOMAIN = 'internetmedicin.se'

export function medicalAudienceCookieExists(cookie: string) {
	return cookie
		.split(';')
		.some(item => item.trim().startsWith(`${MEDICAL_AUDIENCE_COOKIE}=`))
}

/**
 * Kaksträngen för `document.cookie`. Domain sätts bara under
 * internetmedicin.se (webbläsaren avvisar annars kakan, t.ex. lokalt), och
 * Secure bara över https så att lokal utveckling via http fungerar.
 */
export function medicalAudienceCookieString(
	hostname: string,
	protocol: string,
) {
	const parts = [
		`${MEDICAL_AUDIENCE_COOKIE}=1`,
		'path=/',
		`max-age=${MAX_AGE_IN_SECONDS}`,
		'SameSite=Lax',
	]
	if (hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`)) {
		parts.push(`domain=.${SHARED_DOMAIN}`)
	}
	if (protocol === 'https:') parts.push('Secure')
	return parts.join('; ')
}

function setMedicalAudienceCookie() {
	document.cookie = medicalAudienceCookieString(
		window.location.hostname,
		window.location.protocol,
	)
}

// Huvudsidan använder Headless UI:s Transition; samma rörelse med CSS här så
// att assistenten slipper ett nytt beroende.
const ENTER_DELAY_MS = 1000
const LEAVE_MS = 200

const PHASE_CLASSES = {
	before: 'translate-y-full scale-50 opacity-0',
	shown: '[transition-duration:400ms] -translate-y-20 scale-100 opacity-100',
	leaving: 'scale-95 opacity-0 duration-200 ease-in-out',
}

export function OnlyForMedicalAlert() {
	const [phase, setPhase] = useState<'hidden' | keyof typeof PHASE_CLASSES>(
		'hidden',
	)

	// Visas bara med JavaScript påslaget, som på huvudsidan.
	useEffect(() => {
		if (medicalAudienceCookieExists(document.cookie)) {
			setMedicalAudienceCookie()
			return
		}
		setPhase('before')
		const timer = window.setTimeout(() => setPhase('shown'), ENTER_DELAY_MS)
		return () => window.clearTimeout(timer)
	}, [])

	useEffect(() => {
		if (phase !== 'leaving') return
		const timer = window.setTimeout(() => setPhase('hidden'), LEAVE_MS)
		return () => window.clearTimeout(timer)
	}, [phase])

	if (phase === 'hidden') return null
	const shown = phase === 'shown'

	return (
		<div
			className={`pointer-events-none fixed inset-x-0 bottom-20 z-50 flex transform justify-center transition ${PHASE_CLASSES[phase]}`}
		>
			<div data-nosnippet role="alert" aria-hidden={!shown}>
				<div
					className={`${shown ? 'pointer-events-auto' : ''} flex w-[500px] max-w-[calc(100dvw_-_40px)] flex-col space-y-3 rounded-xl border border-2 border-green-700 bg-white bg-opacity-80 px-4 py-6 text-green-800 shadow-lg backdrop-blur backdrop-filter dark:border-green-800 dark:bg-gray-800 dark:text-white lg:space-y-4`}
				>
					<span className="block text-lg font-bold">
						För läkare och sjuksköterskor
					</span>
					<span className="block text-base text-gray-800 dark:text-white">
						Denna tjänst är ett beslutsstöd i den kliniska vardagen och endast
						avsedd för <strong>läkare</strong> och{' '}
						<strong>sjuksköterskor</strong> med förskrivningsrätt.
					</span>
					<span className="block text-base text-gray-800 dark:text-white">
						Läs mer i vår{' '}
						<a
							className="inline-flex items-center text-blue-600 dark:text-blue-500"
							href="https://www.internetmedicin.se/privacy"
							target="_blank"
							rel="noopener noreferrer"
						>
							<span className="hover:underline">
								Integritets- & Cookiepolicy
							</span>
							<ArrowTopRightOnSquareIcon
								className="ml-1 h-5 w-5 flex-none"
								aria-hidden="true"
							/>
						</a>
						.
					</span>
					<span className="block pb-3 text-base text-gray-800 dark:text-white">
						Information till patient eller anhörig hänvisas till{' '}
						<a
							className="inline-flex items-center text-blue-600 dark:text-blue-500"
							href="https://www.1177.se"
							rel="noopener noreferrer"
						>
							<span className="hover:underline">1177.se</span>
							<ArrowTopRightOnSquareIcon
								className="ml-1 h-5 w-5 flex-none"
								aria-hidden="true"
							/>
						</a>
						.
					</span>
					<button
						type="button"
						onClick={() => {
							setMedicalAudienceCookie()
							setPhase('leaving')
							window.dispatchEvent(new Event(MEDICAL_AUDIENCE_ACCEPTED_EVENT))
						}}
						className="mb-2 mr-2 rounded-lg bg-green-700 px-5 py-2.5 text-center text-sm font-semibold text-white hover:bg-green-800 focus:outline-none focus:ring-4 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
					>
						OK, fortsätt
					</button>
				</div>
			</div>
		</div>
	)
}
